/**
 * <AIGenerateButton /> — reusable "Generate with AI" control for
 * free-text fields (M21.11, criterion 17).
 *
 * Click → opens a modal that immediately drafts text from a
 * context-aware prompt (POST /api/ai-assist/generate). The user can
 * edit the draft inline, Regenerate, or accept it ("Use this
 * draft") — at which point `onAccept` fires with the final text and
 * the modal closes. Provenance (model + prompt name/version) is
 * shown so the caller can record who-generated-what.
 *
 * Commandment 23: this only ever produces a DRAFT. Persisting is the
 * caller's job, behind a human accept.
 *
 * Used on Job descriptions, Termination reasons, and Leave-request
 * reasons.
 */

import { useMutation } from "@tanstack/react-query";
import { Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "../../lib/api";
import type { AiGenerateResponse } from "../../lib/api";

type GenerateKind =
  | "job_description"
  | "termination_reason"
  | "leave_request_reason";

export interface AIGenerateButtonProps {
  kind: GenerateKind;
  legalEntityId: string | undefined;
  /** Context kwargs the prompt needs (e.g. job_title, job_family). */
  context: Record<string, string>;
  /** Fired with the final (possibly edited) draft when accepted. */
  onAccept: (draft: string) => void;
  label?: string;
  disabled?: boolean;
}

export function AIGenerateButton({
  kind,
  legalEntityId,
  context,
  onAccept,
  label,
  disabled,
}: AIGenerateButtonProps) {
  const { t } = useTranslation();
  const effectiveLabel = label ?? t("app.agb_label");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [provenance, setProvenance] = useState<AiGenerateResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const generateM = useMutation({
    mutationFn: () => {
      if (!legalEntityId) {
        return Promise.reject(new Error(t("app.agb_no_le")));
      }
      return api.aiGenerate({
        kind,
        legal_entity_id: legalEntityId,
        context,
      });
    },
    onSuccess: (res) => {
      setError(null);
      setProvenance(res);
      setDraft(res.draft);
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.detail : String(e)),
  });

  // Kick off the first generation when the modal opens.
  useEffect(() => {
    if (open) {
      setDraft("");
      setProvenance(null);
      setError(null);
      generateM.mutate();
    }
    // generateM identity is stable enough for this open-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const noEntity = !legalEntityId;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled || noEntity}
        title={
          noEntity
            ? "Select a legal entity to use AI generation"
            : undefined
        }
        className="inline-flex items-center gap-1.5 rounded-lg border border-brand/40 bg-brand/5 px-2.5 py-1.5 text-xs font-medium text-brand hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Sparkles size={13} />
        {effectiveLabel}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/40 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 p-4">
              <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                <Sparkles size={16} className="text-brand" />
                {t("app.agb_title")}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("app.agb_close")}
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3 p-4">
              {generateM.isPending ? (
                <p className="py-8 text-center text-sm text-slate-500">
                  {t("app.agb_drafting")}
                </p>
              ) : error ? (
                <p className="rounded bg-rose-50 p-3 text-sm text-rose-700">
                  {error}
                </p>
              ) : (
                <>
                  <textarea
                    rows={8}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  {provenance && (
                    <p className="text-xs text-slate-400">
                      {t("app.agb_provenance", {
                        model: provenance.model,
                        prompt: provenance.prompt_name,
                        version: provenance.prompt_version,
                      })}
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 p-4">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {t("app.agb_cancel")}
              </button>
              <button
                type="button"
                onClick={() => generateM.mutate()}
                disabled={generateM.isPending}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {t("app.agb_regenerate")}
              </button>
              <button
                type="button"
                onClick={() => {
                  onAccept(draft);
                  setOpen(false);
                }}
                disabled={generateM.isPending || !draft.trim()}
                className="rounded-lg bg-brand px-3 py-2 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
              >
                {t("app.agb_use_draft")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
